import { Box, ButtonProps, Flex, MenuItemProps } from "@chakra-ui/react";
import { useState } from "react";
import { useIntl } from "react-intl";

import { RanaModal } from "@nens/ranalib";
import { RanaForm } from "@nens/ranalib";

import { EditCancelSubmitButtons } from "@/components/edit-cancel-submit-buttons";
import { MarkdownExplanation } from "@/components/markdown-explanation";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";

import { usePublicationEditModal } from "../hooks/use-edit";

export type PublicationFormValues = {
  name: string;
  description: string;
};

type PublicationDetailEditFormProps = ReturnType<typeof usePublicationEditModal> & {
  buttonText: string;
  initialValues: PublicationFormValues;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  buttonProps?: ButtonProps | MenuItemProps;
};

export function PublicationDetailEditForm({
  validate,
  submit,
  isMutating,
  buttonText,
  initialValues,
  isOpen,
  onClose,
  onOpen,
  buttonProps,
}: PublicationDetailEditFormProps) {
  const [isPreviewing, setIsPreviewing] = useState(false);
  const intl = useIntl();

  const { canWriteProject } = useProjectActionAllowed();
  if (!canWriteProject) return null;

  return (
    <>
      <Box {...buttonProps} onClick={onOpen}>
        {buttonText}
      </Box>

      <RanaModal isOpen={isOpen} onClose={onClose} topBar={buttonText}>
        <RanaForm initialValues={initialValues} validate={validate} doSubmit={submit}>
          <RanaForm.Input
            field="name"
            isRequired
            label={intl.formatMessage({
              id: "newpublication.name.label",
              defaultMessage: "Title",
            })}
          />

          <RanaForm.Textarea
            field="description"
            label={intl.formatMessage({
              id: "newpublication.description.label",
              defaultMessage: "Description",
            })}
            minH={52}
            isPreviewingMarkdown={isPreviewing}
          />

          <Flex
            gap={4}
            alignItems="center"
            direction="row"
            w="full"
            my={4}
            justifyContent="flex-end"
          >
            <MarkdownExplanation />
            {canWriteProject && (
              <EditCancelSubmitButtons
                isMutating={isMutating}
                isEditing
                isPreviewing={isPreviewing}
                previewButtonProps={{ onClick: () => setIsPreviewing(true), variant: "secondary" }}
                submitButtonProps={{
                  as: RanaForm.SubmitButton,
                  isLoading: isMutating,
                  variant: "primary",
                }}
                cancelButtonProps={{ onClick: onClose, variant: "tertiary" }}
                editButtonProps={{ onClick: () => setIsPreviewing(false), variant: "secondary" }}
              />
            )}
          </Flex>
          <RanaForm.Errors />
        </RanaForm>
      </RanaModal>
    </>
  );
}
